import { createReducer } from '@reduxjs/toolkit';
import { SiteDto, IDemandOrderDto, DemandOrderDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    createNewEmptyDemandOrder,
    createNewDemandOrder,
    clearDemandOrder,
    saveDemandOrder,
    importDemandOrder,
    removeDemandOrderDestinationSite,
    setDemandOrderDestinationSite,
    updateDemandOrderData,
    updateDemandOrderDescription,
} from '../actions/demandOrderActions';
import {
    clearBaseEntityState,
    compareModifiedDate,
    IBaseEntityState,
    IModifiedInfo,
    SaveEntityStatus,
    setBaseEntityCurrentJson,
} from './common';

export interface DemandOrderFormReducerState extends IBaseEntityState {
    demandOrder: IDemandOrderDto | null;
}

function clearState(state: DemandOrderFormReducerState): void {
    state.demandOrder = null;

    clearBaseEntityState(state);
}

function getInitialState(): DemandOrderFormReducerState {
    const state: DemandOrderFormReducerState = {
        demandOrder: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

function calculateCurrentJson(state: DemandOrderFormReducerState, setAsBaseline: boolean = false): void {
    myAssert(() => state.demandOrder != null, 'Need order to calculate');

    const jsonBaseline = JSON.stringify(state.demandOrder);

    setBaseEntityCurrentJson(state, jsonBaseline, setAsBaseline);
}

function calculateBaseInfo(state: DemandOrderFormReducerState) {
    myAssert(() => state.demandOrder != null, 'Need order to calculate');

    if (!state.demandOrder!.id || state.demandOrder!.id <= 0) {
        // no modified data should be set
    } else {
        // lets find the last modified part of order
        const modified: IModifiedInfo | undefined = compareModifiedDate(state.demandOrder, undefined);

        state.modified =
            (modified ?? state.demandOrder!.created)
                ? {
                      modifiedAt: state.demandOrder!.created ?? new Date(),
                      modifiedBy: state.demandOrder!.createdBy ?? '',
                  }
                : undefined;
    }
}

export const demandOrderFormReducer = createReducer<DemandOrderFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(createNewEmptyDemandOrder, (state: DemandOrderFormReducerState, action: any) => {
                clearState(state);

                const newOrder: IDemandOrderDto = new DemandOrderDto();

                newOrder.id = 0;

                state.demandOrder = newOrder;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(createNewDemandOrder, (state: DemandOrderFormReducerState, action: any) => {
                clearState(state);

                const payload: IDemandOrderDto = action.payload;

                // check order data
                myAssert(() => payload.id === 0, 'It is new order!!!');

                state.demandOrder = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(importDemandOrder.fulfilled, (state: DemandOrderFormReducerState, action: any) => {
                clearState(state);

                const payload: IDemandOrderDto = action.payload;

                myAssert(() => !!payload, 'Required Data for Import');

                state.demandOrder = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(clearDemandOrder, (state: DemandOrderFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(setDemandOrderDestinationSite, (state: DemandOrderFormReducerState, action: any) => {
                myAssert(() => state.demandOrder !== null, 'There needs to be order if it is being updated');

                const site = action.payload as SiteDto;

                state.demandOrder! = { ...state.demandOrder, destinationSite: site, destinationSiteId: site?.id };

                calculateCurrentJson(state);
            })
            .addCase(removeDemandOrderDestinationSite, (state: DemandOrderFormReducerState, action: any) => {
                myAssert(() => state.demandOrder !== null, 'There needs to be order if it is being updated');

                state.demandOrder! = { ...state.demandOrder, destinationSite: undefined, destinationSiteId: undefined };

                calculateCurrentJson(state);
            })
            .addCase(updateDemandOrderDescription, (state: DemandOrderFormReducerState, action: any) => {
                myAssert(() => state.demandOrder !== null, 'There needs to be order if it is being updated');

                state.demandOrder! = { ...state.demandOrder, description: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateDemandOrderData, (state: DemandOrderFormReducerState, action: any) => {
                myAssert(() => state.demandOrder !== null, 'There needs to be order if it is being updated');

                state.demandOrder! = { ...state.demandOrder, data: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(saveDemandOrder.pending, (state: DemandOrderFormReducerState, action: any) => {
                myAssert(() => state.demandOrder !== null, 'There needs to be order if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(saveDemandOrder.fulfilled, (state: DemandOrderFormReducerState, action: any) => {
                myAssert(() => state.demandOrder !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(saveDemandOrder.rejected, (state: DemandOrderFormReducerState, action: any) => {
                myAssert(() => state.demandOrder !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
